import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Divider,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import IPS_Icon from '../../assets/IPS_Icon+Type_CMYK.png';
import Image from 'mui-image';

interface ButtonLinkProps {
  href: string;
  label: string;
}

const ButtonLink: React.FC<ButtonLinkProps> = ({ href, label }) => (
  <Button
    size="small"
    variant="text"
    color="primary"
    href={href}
    target="_blank"
    rel="noopener"
    sx={{
      textTransform: 'none',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(5, 195, 222, 0.08)',
      },
      color: '#0072CE',
    }}
  >
    {label}
  </Button>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`ips-tabpanel-${index}`}
    aria-labelledby={`ips-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const IPSLandingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Hero Section with IPS Logo */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #05C3DE 0%, #0072CE 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Image
            src={IPS_Icon}
            alt="IPS Logo"
            sx={{ maxWidth: '100px' }}
            width={'40vw'}
          />
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1.5, color: '#fff' }}
        >
          IPS (International Peace Scholarship)
        </Typography>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', color: '#fff' }}
        >
          The P.E.O. International Peace Scholarship Fund (IPS) is a fund
          established in 1949 to provide scholarships for women from other
          countries for graduate study in the U.S. or Canada. The maximum award
          amount is $12,500.
        </Typography>
      </Paper>

      {/* Tab Navigation */}
      <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="IPS page tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                minHeight: 64,
              },
            }}
          >
            <Tab label="Information & Resources" />
          </Tabs>
        </Box>

        {/* Information & Resources Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Local Chapter Process */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Process"
              subheader="All applicants must complete and submit the online eligibility form. The eligibility period is September 15 to December 15. The form is on the public-facing side of the website."
              sx={{
                background: 'linear-gradient(135deg, #05C3DE 0%, #0072CE 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ pb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #05C3DE 0%, #0072CE 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Step
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #05C3DE 0%, #0072CE 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Resources
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Potential applicants submit the eligibility form during
                      the eligibility window.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item container xs={12} md={6} spacing={1}>
                  <Grid item>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/df3c5ec1172d4cdcaf75e1e47dbbbd5d/latest"
                      label="Application Calendar"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Once approved, applicants submit the completed application
                      materials online by the appropriate deadline.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ButtonLink
                    href="https://peo-portal.com/library/document/c2fb3dc8cb4e4f399f58e74606ab69f5/latest"
                    label="IPS Board of Trustees Student Selection Process"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Announcement of awards is made in May.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ButtonLink
                    href="https://peo-portal.com/library/document/3cb1c730b35c4beca7b60878b265e203/latest"
                    label="P.E.O. International Peace Scholarship Fund Policies and Procedures"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Eligibility Requirements */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <CardHeader
              title="Eligibility Requirements"
              subheader="A woman is eligible for consideration for an International Peace Scholarship if she:"
              sx={{
                background: 'linear-gradient(135deg, #05C3DE 0%, #0072CE 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      is a woman outside the United States and Canada who is
                      seeking an advanced degree from an accredited college or
                      university in the U.S. or Canada;
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                      ml: 2,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      will return to her home country to utilize her education
                      to make a positive impact and foster global peace; and
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                      ml: 2,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      has a full year of formal coursework remaining and is
                      enrolled and in residence in the U.S. or Canada for the
                      entire school year; or
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      is an international student attending Cottey College
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Meets the required legal definition of a woman as
                      specified in Iowa Code Chapter 4.1A1b and 4.1A1c.
                      Effective July 15, 2026, this requirement has been paused
                      pending further review.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ p: 1.5, lineHeight: 1.6, fontWeight: 'bold' }}
                  >
                    Please Note:
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Doctoral students who have completed coursework and are
                      working only on dissertations are not eligible as
                      first-time applicants.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      A student who is a citizen or permanent resident of the
                      United States or Canada is not eligible. A student with
                      residency for tax purposes only is permitted.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Scholarships are not given for online courses, research,
                      internships or for practical training if not combined with
                      formal coursework.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      At the time of application, the applicant is required to
                      indicate additional financial resources adequate to meet
                      her estimated expenses. (ie: personal and family funds,
                      tuition waivers, work, scholarships, teaching
                      assistantships, study grants and other scholarships)
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#0072CE',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      The maximum amount awarded to a student is $12,500. Lesser
                      amounts may be awarded according to individual needs.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Eligibility Requirements */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <CardHeader
              title="Partners in Peace Program"
              sx={{
                background: 'linear-gradient(135deg, #05C3DE 0%, #0072CE 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      The P.E.O. Partners in Peace Program offers the
                      opportunity for local chapters to help women from around
                      the world change the trajectory of their lives by pursuing
                      graduate degrees in the U.S. and Canada. By making a
                      lump-sum contribution of $500 or more, a chapter can be
                      proud to be a “Partners in Peace Chapter” and help foster
                      global peace through education.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      To become a Partners in Peace Chapter, make sure your
                      chapter's contribution reaches your state, provincial or
                      district (s/p/d) treasurer or executive assistant by
                      January 31. (Chapters in Hawaii should send their
                      contributions to the International Chapter Treasurer).
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Partners in Peace Chapters also have the unique
                      opportunity to get to know a remarkable international
                      woman by submitting the Partners in Peace Student Match
                      Form to be partnered with a student for the upcoming
                      academic year. This is an opportunity for an IPS recipient
                      to experience the warmth of P.E.O. and learn more about
                      our culture and for P.E.O.s to learn about the recipient,
                      her studies and her homeland. A special bond often
                      develops with the student, who may become an ambassador
                      for peace upon returning to her country.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Whether the $500 donation is from a chapter or individual,
                      chapters must submit the Partners in Peace Student Match
                      Form to be matched with a student. The form no longer is
                      submitted to the s/p/d treasurer. It is online and goes
                      directly to the IPS office in Des Moines. Chapters must be
                      sure to submit the form by January 31 to be considered for
                      a student match the following fall.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ pb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} textAlign="center">
                  <Link
                    href="https://peointernational.formstack.com/forms/partners_in_peace_match_form"
                    target="_blank"
                    rel="noopener"
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #05C3DE 0%, #0072CE 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        opacity: 0.6,
                      }}
                    >
                      Partners in Peace Student Match Form
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            </CardActions>
          </Card>
        </TabPanel>
      </Card>
    </Container>
  );
};

export default IPSLandingPage;
